import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, BellRing } from "lucide-react";
import { api, errMsg, payText } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageTitle, Card, BigButton } from "@/components/Layout";
import { Switch } from "@/components/ui/switch";
import { PaymentFields } from "@/components/PaymentFields";
import { MapPicker } from "@/components/MapPicker";
import { AddressFields, PrivacyNotice } from "@/components/Fields";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { enablePush, pushSupported } from "@/lib/push";
import { useLogout } from "@/pages/Profile";
import { ChangePasswordForm } from "@/components/ChangePassword";
import { UsernameForm } from "@/components/UsernameForm";

const NOTIF_LABELS = { booked: "Pickup booked", confirmed: "Pickup confirmed", changed: "Pickup changed", completed: "Pickup completed",
  cancelled: "Pickup cancelled", complaint: "Complaint replies", reminder: "Pickup reminders" };

const SwitchRow = ({ label, checked, onChange, testid }) => (
  <label className="flex min-h-[52px] items-center justify-between gap-3">
    <span className="font-medium text-[#0D3B2B]">{label}</span>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} className="data-[state=checked]:bg-[#2E9E6B]" />
  </label>
);

export default function Settings() {
  const { user, setUser, t, lang, setLang, meta } = useApp();
  const nav = useNavigate();
  const logout = useLogout();
  const [s, setS] = useState(null);
  const [pay, setPay] = useState(null);
  const [payForm, setPayForm] = useState({ payment_method: "Cash", upi_id: "", bank_holder: "", bank_account: "", bank_ifsc: "" });
  const [loc, setLoc] = useState({ lat: user.lat, lng: user.lng, address: user.address || "", landmark: user.landmark || "" });
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (window.location.search.includes("reset=1")) setTimeout(() => document.getElementById("password")?.scrollIntoView({ behavior: "smooth" }), 600);
  }, []);

  useEffect(() => {
    api.get("/settings").then(({ data }) => { setS(data.settings); setPay(data.payment); setPayForm((f) => ({ ...f, payment_method: data.payment.method })); });
  }, []);

  const saveSettings = async (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    try { await api.put("/settings", patch); setUser({ ...user, settings: next }); } catch (e) { toast.error(errMsg(e)); }
  };

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); setConfirm(null); } catch (e) { toast.error(errMsg(e)); setConfirm(null); } finally { setBusy(false); }
  };

  const actions = {
    payment: { title: t("Are you sure you want to change your payment details?"),
      lines: [`${t(payText(pay))} → ${t(payForm.payment_method === "Bank" ? "Online bank transfer" : payForm.payment_method)}`],
      fn: () => run(async () => {
        const { data } = await api.put("/settings/payment", payForm);
        setPay(data.payment);
        setPayForm({ payment_method: data.payment.method, upi_id: "", bank_holder: "", bank_account: "", bank_ifsc: "" });
        toast.success(t("Payment details saved"));
      }) },
    location: { title: t("Are you sure you want to change your saved location?"), lines: [`${user.address || "—"} → ${loc.address}`],
      fn: () => run(async () => {
        const keys = ["customer_type", "full_name", "business_name", "contact_person", "mobile", "alt_mobile", "contact_email", "area"];
        const { data } = await api.put("/profile", { ...Object.fromEntries(keys.map((k) => [k, user[k] || ""])), ...loc });
        setUser(data);
        toast.success(t("Saved location updated"));
      }) },
    delete: { title: t("Are you sure you want to delete your account?"), danger: true,
      lines: [t("Your login and profile will be removed. Past pickup and complaint records are kept for Kindsaver's accounts.")],
      fn: () => run(async () => { await api.delete("/account"); setUser(false); nav("/login", { replace: true }); }) },
  };

  const turnOnPush = async () => {
    try { await enablePush(meta.vapid_public_key); toast.success(t("Push notifications are on")); } catch (e) { toast.error(t(e.message)); }
  };

  if (!s) return <p className="text-[#7B968B]">{t("Loading…")}</p>;
  const a = confirm && actions[confirm];

  return (
    <div className="space-y-5">
      <PageTitle>{t("Settings")}</PageTitle>
      <Card className="space-y-3">
        <h2 className="font-display text-lg font-bold">{t("Language")}</h2>
        <div className="flex rounded-2xl bg-[#EAEFEA] p-1">
          {[["en", "English"], ["hi", "हिंदी"]].map(([v, l]) => (
            <button key={v} onClick={() => setLang(v)} data-testid={`lang-${v}-btn`}
              className={`h-11 flex-1 rounded-xl font-bold ${lang === v ? "bg-white text-[#0D3B2B] shadow-sm" : "text-[#4A6358]"}`}>{l}</button>
          ))}
        </div>
      </Card>
      <Card className="space-y-1">
        <h2 className="mb-2 font-display text-lg font-bold">{t("Notifications")}</h2>
        {Object.entries(NOTIF_LABELS).map(([k, l]) => (
          <SwitchRow key={k} label={t(l)} checked={s.notif[k] !== false} testid={`notif-switch-${k}`}
            onChange={(v) => saveSettings({ notif: { ...s.notif, [k]: v } })} />
        ))}
        {pushSupported() && (
          <button onClick={turnOnPush} data-testid="enable-push-btn" className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-2xl border-2 border-[#2E9E6B] font-semibold text-[#2E9E6B]">
            <BellRing className="h-5 w-5" /> {t("Turn on phone notifications")}
          </button>
        )}
      </Card>
      <Card>
        <h2 className="mb-1 font-display text-lg font-bold">{t("Leaderboard")}</h2>
        <SwitchRow label={t("Hide me from the leaderboard")} checked={!!s.hide_leaderboard} testid="hide-leaderboard-switch"
          onChange={(v) => saveSettings({ hide_leaderboard: v })} />
      </Card>
      <Card className="space-y-3">
        <h2 className="font-display text-lg font-bold">{t("Default payment choice")}</h2>
        <p className="text-sm text-[#4A6358]" data-testid="settings-current-payment">{t("Current")}: {t(payText(pay))}</p>
        <PaymentFields form={payForm} set={(p) => setPayForm((f) => ({ ...f, ...p }))} saved={pay} />
        <BigButton onClick={() => setConfirm("payment")} data-testid="settings-save-payment-btn">{t("Save payment details")}</BigButton>
      </Card>
      <Card className="space-y-3">
        <h2 className="font-display text-lg font-bold">{t("Saved location")}</h2>
        <MapPicker lat={loc.lat} lng={loc.lng} onChange={(lat, lng) => setLoc((l) => ({ ...l, lat, lng }))} />
        <AddressFields form={loc} set={(p) => setLoc((l) => ({ ...l, ...p }))} />
        <BigButton onClick={() => (loc.address.trim() ? setConfirm("location") : toast.error(t("Please enter the pickup address.")))} data-testid="settings-save-location-btn">{t("Save location")}</BigButton>
      </Card>
      <Card className="space-y-3" id="username" data-testid="settings-username-card">
        <h2 className="font-display text-lg font-bold">{user.username ? t("Your username") : t("Create a username")}</h2>
        <p className="text-sm text-[#4A6358]">{t("Optional. With a username and password you can also log in without an SMS code.")}</p>
        <UsernameForm />
      </Card>
      <Card className="space-y-3" id="password" data-testid="settings-password-card">
        <h2 className="font-display text-lg font-bold">{user.has_password ? t("Change password") : t("Set a password")}</h2>
        <p className="text-sm text-[#4A6358]">{user.username ? `@${user.username} · ` : ""}{t("Log in with your username and this password.")}</p>
        <ChangePasswordForm />
      </Card>
      <PrivacyNotice />
      <div className="space-y-3">
        <BigButton onClick={logout} data-testid="settings-logout-btn"><LogOut className="h-5 w-5" />{t("Log out")}</BigButton>
        <button onClick={() => setConfirm("delete")} data-testid="settings-delete-account-btn" className="flex h-12 w-full items-center justify-center gap-2 font-semibold text-[#E05C3A]">
          <Trash2 className="h-5 w-5" />{t("Delete my account")}
        </button>
      </div>
      <ConfirmDialog open={!!a} onOpenChange={(o) => !o && setConfirm(null)} title={a?.title} lines={a?.lines} danger={a?.danger} busy={busy} onYes={a?.fn} />
    </div>
  );
}
